import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';

import { environment } from 'src/environments/environment';

import { AgentBankCredentialsApiService } from 'src/app/services/agent-bank-credentials-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { AgentBankCredential } from 'src/app/shared/agent-bank-credentials';

/** Banking integration P1's own deliberate choice: no `CHECK` constraint on
 *  `bank_name` at the DB level, for future flexibility. A free-text
 *  *frontend* field would be one typo/casing variant away from an adapter's
 *  exact-match lookup (P5/P6, `webyto-agent`) silently finding zero rows for
 *  an entity that actually has a credential configured — a fixed dropdown
 *  closes that risk at the UI layer without touching the DB constraint. */
const BANK_OPTIONS: { label: string; value: string }[] = [
  { label: 'Ukreximbank', value: 'Ukreximbank' },
  { label: 'PrivatBank', value: 'PrivatBank' },
];

/**
 * Banking integration chunk P3: `AgentBankCredentials` admin tab. Lives in
 * `EditUserAccessComponent` (IT/infra setup), not `HomeComponent` (day-to-day
 * operational review) — see `docs/PROMPT_agent-bank-credentials-tab.md`'s
 * "Where this lives" section for the two real precedents this was checked
 * against before deciding.
 *
 * Mirrors `edit-agent-team-members-tab.component.ts`'s shape (inline
 * row-editing table, single-edit-at-a-time via `editMode`, `uid`/`cloneIndex`
 * bookkeeping, a `uid: -2` sentinel for an unsaved new row) rather than
 * re-deriving the pattern. **Corrected 2026-09-06 (owner, live click-test)**:
 * §6's original P3 decision called the `CLIID` picker "unrestricted" — read
 * at the time as the full `Clients` list, matching `getCliList()`. A live
 * click-test showed that dropdown listing every customer/vendor in the
 * database (thousands of rows), not just PROXIS's own legal entities — not
 * what was intended. The picker (`app-companyselector`) is scoped the same
 * way the team-members sibling scopes its own "preferred company" picker:
 * `[parentCompany]="mainCompanyId"` (`GetglobalsettingsService.getMainCompanyId()`),
 * restricting it to children of the main company — i.e. PROXIS's own legal
 * entities (§1a: `CLIID`s 5016/5758/6139/6435/7750), not arbitrary `Clients`
 * rows. It only ever writes `cliid` directly, with no extra display-name
 * lookup call on selection — the read-only cell resolves the name from
 * `cliOptions` (loaded once via `ClientApiService.getCliList()`, the full
 * list — the read-only *display* lookup is unrestricted on purpose, since a
 * saved row could in principle reference any `CLIID`, and this cell only
 * needs a name to show, not a picker) the same way `companyName()` does on
 * the team-members sibling.
 *
 * **Never displays or accepts the actual bank secret.** `credential_key` is
 * a reference name into a per-bank `.env.*.secrets` file on the server,
 * added there manually, entirely outside this component's scope.
 */
@Component({
  selector: 'app-edit-agent-bank-credentials-tab',
  templateUrl: './edit-agent-bank-credentials-tab.component.html',
  styleUrl: './edit-agent-bank-credentials-tab.component.css',
  encapsulation: ViewEncapsulation.None
})
export class EditAgentBankCredentialsTabComponent implements OnInit, OnDestroy {

  bankCredentials: AgentBankCredential[] = [];
  bankCredentialsClone: AgentBankCredential[] = [];
  cliOptions: { label: string; value: number }[] = [];
  bankOptions = BANK_OPTIONS;
  mainCompanyId: number = null;

  /** `true` while any row is being edited -- same single-row-edit-at-a-time
   *  convention as `edit-agent-team-members-tab.component.ts`'s own
   *  `editMode`. Gates every other row's pencil button plus "Оновити"/
   *  "Новий запис" -- reloading or adding mid-edit would replace/mutate
   *  `bankCredentials` out from under `bankCredentialsClone`'s cancel-restore
   *  and PrimeNG's own `editingRowKeys` state. */
  editMode: boolean = false;

  private loadQuery: Subscription = null;
  private cliQuery: Subscription = null;
  private saveQuery: Subscription = null;
  private deleteQuery: Subscription = null;

  @ViewChild('ABC1') bankCredentialsTable: Table;

  constructor(
    private bankCredentialsApi: AgentBankCredentialsApiService,
    private cliApi: ClientApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private messageService: MessageService,
    private router: Router,
    private location: Location,
  ) {
  }

  ngOnDestroy(): void {
    if (this.loadQuery) { this.loadQuery.unsubscribe(); }
    if (this.cliQuery) { this.cliQuery.unsubscribe(); }
    if (this.saveQuery) { this.saveQuery.unsubscribe(); }
    if (this.deleteQuery) { this.deleteQuery.unsubscribe(); }
  }

  ngOnInit(): void {
    this.mainCompanyId = this.globalsettingsservice.getMainCompanyId();
    this.loadBankCredentials();
    this.loadCliOptions();
  }

  /** Loads the full-list entity display-name lookup for the *read-only* cell
   *  -- a saved row could in principle reference any `CLIID`, so this lookup
   *  stays unrestricted (`getCliList()`, not `getCliChildList(mainCompanyId)`)
   *  even though the editable picker itself (`app-companyselector`, template)
   *  is scoped to `mainCompanyId`'s children. Editing resolves its own list
   *  independently -- this is display-only, same as `companyName()` on the
   *  team-members sibling. */
  loadCliOptions(): void {
    this.cliQuery =
      this.cliApi.getCliList().subscribe({
        next: clients => { this.cliOptions = clients.map(c => ({ label: c.CLIENTSNIKNAME, value: c.CLIID })); },
        error: err => {
          console.error('*** EditAgentBankCredentialsTabComponent loadCliOptions: Error: ' + err);
          // Non-fatal: the output cell just falls back to the raw ID, editing still works.
        }
      });
  }

  loadBankCredentials(): void {
    this.loadQuery =
      this.bankCredentialsApi.getBankCredentials().subscribe({
        next: rows => {
          this.bankCredentials = rows.map(r => ({ ...r, uid: r.id, cloneIndex: 0 }));
          environment.production || console.table(this.bankCredentials);
        },
        error: err => {
          console.error('*** EditAgentBankCredentialsTabComponent loadBankCredentials: Error: ' + err);
          this.messageService.add(err?.status === 403
            ? { severity: 'error', summary: 'Недостаточно прав', detail: 'Потрібна роль portal-admin для керування банківськими обліковими даними' }
            : { severity: 'error', summary: 'Не вдалося завантажити список', detail: err?.message || 'Unknown error' });
        }
      });
  }

  addBankCredential(): void {
    const newRow: AgentBankCredential = {
      uid: -2, cloneIndex: 0, id: null, cliid: null,
      bank_name: '', credential_key: '', iban: '',
    };
    this.bankCredentials.push(newRow);
    this.onRowEditInit(newRow);
    this.bankCredentialsTable.initRowEdit(newRow);
  }

  onRowEditInit(row: AgentBankCredential): void {
    let newCloneIndex = 0;
    this.bankCredentialsClone.forEach(r => { newCloneIndex = Math.max(newCloneIndex, r.cloneIndex); });
    row.cloneIndex = ++newCloneIndex;
    this.bankCredentialsClone.push({ ...row });
    this.editMode = true;
  }

  onRowEditCancel(row: AgentBankCredential, index: number): void {
    if (row.uid === -2) {
      this.bankCredentials.splice(index, 1);
    } else {
      this.bankCredentials[index] = { ...this.bankCredentialsClone.find(r => r.cloneIndex === row.cloneIndex) };
      this.bankCredentials[index].cloneIndex = 0;
    }
    this.bankCredentialsClone.splice(this.bankCredentialsClone.findIndex(r => r.cloneIndex === row.cloneIndex), 1);
    this.editMode = false;
  }

  onRowEditSave(row: AgentBankCredential): void {
    // The sibling pSaveEditableRow directive on the same button already exits
    // the table's own row-edit state synchronously on this click, regardless
    // of validation or the save's outcome below -- editMode must follow that,
    // not our async result, or a validation failure or a rejected save would
    // leave every other row's pencil (and "Оновити"/"Новий запис") locked.
    this.editMode = false;

    if (!row.cliid) {
      this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Потрібно обрати компанію' });
      return;
    }
    if (!(row.bank_name || '').trim()) {
      this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Потрібно обрати банк' });
      return;
    }
    if (!(row.credential_key || '').trim()) {
      this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Потрібно вказати credential_key' });
      return;
    }

    const isNew = row.uid === -2;
    this.saveQuery =
      (isNew ? this.bankCredentialsApi.createBankCredential(row) : this.bankCredentialsApi.updateBankCredential(row))
        .subscribe({
          next: result => {
            if (isNew && 'id' in result) { row.id = result.id; }
            row.uid = row.id;
            this.bankCredentialsClone.splice(this.bankCredentialsClone.findIndex(r => r.cloneIndex === row.cloneIndex), 1);
            row.cloneIndex = 0;
          },
          error: err => {
            console.error('*** EditAgentBankCredentialsTabComponent onRowEditSave: Error: ' + err);
            // 409 (duplicate credential_key, or duplicate cliid+bank_name) --
            // surface the backend's own message rather than inventing a
            // separate client-side duplicate check; the DB constraint is the
            // real source of truth and the API already returns a clear
            // message for it.
            this.messageService.add({ severity: 'error', summary: 'Не вдалося зберегти', detail: err?.error?.message || err?.message || 'Unknown error' });
          }
        });
  }

  deleteBankCredential(row: AgentBankCredential): void {
    if (!confirm('Видалити банківський запис "' + row.bank_name + '" для цієї компанії?')) { return; }
    this.deleteQuery =
      this.bankCredentialsApi.deleteBankCredential(row.id).subscribe({
        next: () => { this.loadBankCredentials(); },
        error: err => {
          console.error('*** EditAgentBankCredentialsTabComponent deleteBankCredential: Error: ' + err);
          this.messageService.add({ severity: 'error', summary: 'Не вдалося видалити', detail: err?.error?.message || err?.message || 'Unknown error' });
        }
      });
  }

  cliName(id: number | null): string {
    if (!id) { return ''; }
    return this.cliOptions.find(c => c.value === id)?.label || '';
  }

  reloadTable(): void {
    this.loadBankCredentials();
  }

  /** Opens the row's company in a new browser tab (same behaviour as the client-tier tab). */
  onOpenClient(row: AgentBankCredential): void {
    if (!row.cliid) { return; }
    const path = this.router.serializeUrl(this.router.createUrlTree(['/edit-client', row.cliid]));
    window.open(this.location.prepareExternalUrl(path), '_blank');
  }
}
